import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { uploadDocument, useListDocumentValidityRules } from '../../../services/loanService';
import { downloadDocument } from '../../../services/documentService';
import type { ArchiveDocument } from '../../../services/schemas/loanServiceSchemas/archiveDocument';
import type { UploadDocumentBodyDocumentType } from '../../../services/schemas/loanServiceSchemas/uploadDocumentBodyDocumentType';
import DocumentValidityBadge from '../../common/DocumentValidityBadge';
import { DocumentPreviewModal, useDocumentPreview } from '../../common/DocumentPreview';
import { formatDocumentDate, formatDocumentPeriod, getDocumentUploadDate, getDocumentUploader } from '../../../utils/documentDates';

/**
 * The document archive (business document spec, "Document Management Page Redesign"): a permanent file for every
 * borrower with a disbursed loan, organised by business, then customer documents or one folder per loan.
 */

export const DOCUMENT_LABELS: Record<string, string> = {
  COMPANY_REGISTRATION_CERTIFICATE: 'Company registration',
  PROOF_OF_ADDRESS: 'Proof of address',
  TAX_CLEARANCE: 'Tax compliance status (TCS PIN)',
  OTHER_CORPORATE_DOCUMENT: 'Other corporate document',
  DIRECTOR_ID_COPY: 'Director ID / passport',
  DIRECTOR_PROOF_OF_ADDRESS: 'Director proof of address',
  DIRECTOR_OTHER_DOCUMENT: 'Other director document',
  ID_DOCUMENT: 'ID document',
  COMPANY_REP_ID_COPY: 'Representative ID',
  DELEGATION_OF_AUTHORITY: 'Delegation of authority / mandate',
  BANK_LETTER: 'Bank confirmation letter',
  FINANCIAL_STATEMENTS: 'Financial statements',
  MANAGEMENT_ACCOUNTS: 'Management accounts',
  BANK_STATEMENT: 'Bank statement',
  PAYSLIP: 'Payslip',
  CREDIT_BUREAU_REPORT: 'Credit bureau report',
  DIRECTOR_CONSENT: 'Director consent',
  AFFORDABILITY_ASSESSMENT: 'Affordability assessment',
  EVALUATION_RESULTS: 'Evaluation results',
  APPROVAL_CONDITION_EVIDENCE: 'Approval condition evidence',
  PRE_LOAN_AGREEMENT: 'Pre-agreement',
  LOAN_AGREEMENT: 'Signed loan agreement',
  COLLATERAL_DOCUMENT: 'Collateral document',
  CESSION: 'Cession',
  UNDERTAKING_TO_PAY: 'Undertaking to pay',
  ASSET_VALUATION: 'Asset valuation',
  INSURANCE_QUOTATION: 'Insurance quotation',
  PROOF_OF_PAYMENT: 'Proof of payment',
  MONITORING_DOCUMENT: 'Monitoring document',
  REPAYMENT_EVIDENCE: 'Repayment evidence',
  COLLECTIONS_CORRESPONDENCE: 'Collections correspondence',
  LEGAL_DOCUMENT: 'Legal document',
  CLOSURE_DOCUMENT: 'Settlement / closure document',
  PURCHASE_ORDER: 'Purchase order',
  SUPPLIER_QUOTATION: 'Supplier quotation',
  SUPPLIER_BANKING_DETAILS: 'Supplier banking details',
  INVOICE: 'Invoice',
  PROOF_OF_DELIVERY: 'Proof of delivery',
  OTHER: 'Other',
};

export const documentLabel = (type?: string) =>
  (type && DOCUMENT_LABELS[type]) ||
  (type || 'Document').replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());

export interface FolderDefinition {
  key: string;
  title: string;
  description: string;
  icon: string;
  /** Types that can be uploaded straight into this folder; empty when they come from the application. */
  uploadTypes: string[];
}

export const CUSTOMER_FOLDERS: FolderDefinition[] = [
  { key: 'COMPANY', title: 'Company statutory and trading', icon: 'fa-building-o',
    description: 'Registration, proof of address, TCS PIN and other corporate records.',
    uploadTypes: ['COMPANY_REGISTRATION_CERTIFICATE', 'PROOF_OF_ADDRESS', 'TAX_CLEARANCE', 'OTHER_CORPORATE_DOCUMENT'] },
  { key: 'DIRECTORS', title: 'Directors', icon: 'fa-users',
    description: 'Each director\'s ID, proof of address and other documents, kept per director.',
    uploadTypes: [] },
  { key: 'REPRESENTATIVE', title: 'Company representative', icon: 'fa-id-badge',
    description: 'The representative\'s ID and their mandate or delegation of authority.',
    uploadTypes: ['COMPANY_REP_ID_COPY', 'DELEGATION_OF_AUTHORITY'] },
  { key: 'BANKING', title: 'Banking and financial', icon: 'fa-university',
    description: 'Bank confirmation letter, financial statements and management accounts.',
    uploadTypes: ['BANK_LETTER', 'FINANCIAL_STATEMENTS', 'MANAGEMENT_ACCOUNTS'] },
];

export const LOAN_FOLDERS: FolderDefinition[] = [
  { key: 'APPLICATION', title: 'Application and transaction', icon: 'fa-file-text-o',
    description: 'The request and its transaction documents: purchase orders, quotations, invoices, statements.',
    uploadTypes: ['OTHER'] },
  { key: 'CREDIT', title: 'Credit assessment and approval', icon: 'fa-balance-scale',
    description: 'Bureau reports, consents, assessment results and evidence that approval conditions were met.',
    uploadTypes: ['APPROVAL_CONDITION_EVIDENCE'] },
  { key: 'CONTRACTS', title: 'Contracts and security', icon: 'fa-file-signature',
    description: 'Pre-agreement, signed loan agreement, cessions, collateral and other security.',
    uploadTypes: ['LOAN_AGREEMENT', 'CESSION', 'COLLATERAL_DOCUMENT', 'UNDERTAKING_TO_PAY'] },
  { key: 'DISBURSEMENT', title: 'Disbursement and payment evidence', icon: 'fa-money',
    description: 'The bank\'s proof of each payout.',
    uploadTypes: ['PROOF_OF_PAYMENT'] },
  { key: 'MONITORING', title: 'Post-disbursement monitoring and repayment', icon: 'fa-line-chart',
    description: 'Monitoring reports and evidence of repayments while the loan runs.',
    uploadTypes: ['MONITORING_DOCUMENT', 'REPAYMENT_EVIDENCE'] },
  { key: 'CLOSURE', title: 'Collections, legal and loan closure', icon: 'fa-gavel',
    description: 'Collections correspondence, legal documents and settlement or closure records.',
    uploadTypes: ['COLLECTIONS_CORRESPONDENCE', 'LEGAL_DOCUMENT', 'CLOSURE_DOCUMENT'] },
];

const STATUS: Record<string, { label: string; className: string }> = {
  ACTIVE: { label: 'Active', className: 'bg-success-subtle text-success-emphasis' },
  IN_ARREARS: { label: 'In arrears', className: 'bg-danger-subtle text-danger-emphasis' },
  DEFAULTED: { label: 'Defaulted', className: 'bg-danger-subtle text-danger-emphasis' },
  CLOSED: { label: 'Settled', className: 'bg-secondary-subtle text-secondary-emphasis' },
  WRITTEN_OFF: { label: 'Written off', className: 'bg-dark-subtle text-dark-emphasis' },
};

export const loanStatusLabel = (status?: string) => (status && STATUS[status]?.label) || status || '';

export const LoanStatusPill: React.FC<{ status?: string }> = ({ status }) => (
  <span className={`badge rounded-pill fw-normal ${(status && STATUS[status]?.className) || 'bg-light text-dark'}`}>
    {loanStatusLabel(status)}
  </span>
);

export const initials = (name?: string) =>
  (name || '?').replace(/\(.*?\)/g, '').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();

export const formatDateTime = (value?: string) =>
  value ? new Date(value).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

export const formatRand = (value?: number) =>
  value == null ? '' : `R${Math.round(value).toLocaleString('en-ZA').replace(/\s/g, ',')}`;

/** The green banner every list page uses (as on Loan Accounts), with the archive breadcrumb and title. */
export const ArchiveHeader: React.FC<{
  crumbs: { label: string; to?: string }[];
  title: string;
  subtitle?: React.ReactNode;
  backTo?: string;
  actions?: React.ReactNode;
}> = ({ crumbs, title, subtitle, backTo, actions }) => (
  <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-4 bg-primary-gradient">
    <div className="container-fluid">
      <div className="row g-3 mb-3 align-items-center">
        <div className="col">
          <ol className="breadcrumb bg-transparent mb-0">
            <li className="breadcrumb-item">
              <Link className="text-white" to="/"><i className="fa fa-home me-2"></i>Home</Link>
            </li>
            {crumbs.map((c, i) => (
              <li key={i} className={`breadcrumb-item ${c.to ? '' : 'active'} text-white`} aria-current={c.to ? undefined : 'page'}>
                {c.to ? <Link className="text-white" to={c.to}>{c.label}</Link> : c.label}
              </li>
            ))}
          </ol>
        </div>
      </div>
      <div className="row align-items-center">
        <div className="col-md-8">
          {backTo && (
            <Link to={backTo} className="small text-white text-decoration-none opacity-75 d-inline-block mb-1">
              <i className="fa fa-arrow-left me-1"></i>Back one level
            </Link>
          )}
          <div className="text-uppercase small fw-semibold text-white opacity-75 mb-1" style={{ letterSpacing: '0.12em' }}>
            Document management <span className="mx-1">•</span>Post-disbursement records
          </div>
          <h1 className="fs-2 fw-bold mb-2 text-white">{title}</h1>
          {subtitle && <div className="mb-0 text-white fs-6 opacity-75">{subtitle}</div>}
        </div>
        {actions && <div className="col-md-4 text-md-end mt-2 mt-md-0">{actions}</div>}
      </div>
    </div>
  </div>
);

export const StatCard: React.FC<{ label: string; value: number | string; hint: string; icon: string; tone?: 'green' | 'amber' }> =
  ({ label, value, hint, icon, tone = 'green' }) => (
    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 14 }}>
      <div className="card-body d-flex justify-content-between">
        <div>
          <div className="text-uppercase small text-muted fw-semibold" style={{ letterSpacing: '0.08em' }}>{label}</div>
          <div className="fs-2 fw-bold">{value}</div>
          <div className="small text-muted">{hint}</div>
        </div>
        <div className={`rounded-3 d-flex align-items-center justify-content-center ${tone === 'amber' ? 'bg-warning-subtle text-warning-emphasis' : 'bg-success-subtle text-success-emphasis'}`}
          style={{ width: 46, height: 46 }}>
          <i className={`fa ${icon} fs-5`}></i>
        </div>
      </div>
    </div>
  );

/**
 * A folder's documents: current ones first with their status and who uploaded them when, previous versions
 * behind a toggle so a replacement never hides what came before; view and download each; and upload into the
 * folder where its documents are not collected on the application.
 */
export const ArchiveFolderDocuments: React.FC<{
  folder: FolderDefinition;
  documents: ArchiveDocument[];
  /** The application a new document is attached to; uploading is off without one. */
  uploadApplicationId?: string;
  onUploaded?: () => void;
}> = ({ folder, documents, uploadApplicationId, onUploaded }) => {
  const preview = useDocumentPreview(message => toast.error(message));
  const [showPrevious, setShowPrevious] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [uploadType, setUploadType] = useState(folder.uploadTypes[0] || '');
  const [documentDate, setDocumentDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [uploading, setUploading] = useState(false);
  const { data: rules } = useListDocumentValidityRules({ query: { staleTime: 10 * 60_000 } });
  const rule = (rules || []).find(r => r.documentType === uploadType);

  const current = useMemo(() => documents.filter(d => d.document?.status !== 'SUPERSEDED'), [documents]);
  const previous = useMemo(() => documents.filter(d => d.document?.status === 'SUPERSEDED'), [documents]);

  const download = async (key?: string, name?: string) => {
    if (!key) return;
    setDownloading(key);
    try {
      const blob = await downloadDocument(key);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = name || 'document';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download the document');
    } finally {
      setDownloading(null);
    }
  };

  const wantsDocumentDate = !!rule?.useDocumentDate && (rule.ruleKind === 'MONTHS' || rule.ruleKind === 'DAYS');
  const wantsExpiry = rule?.ruleKind === 'UNTIL_EXPIRY';
  const dateMissing = !!rule?.requireDocumentDate && !documentDate;

  const upload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !uploadApplicationId || !uploadType) return;
    setUploading(true);
    try {
      await uploadDocument(uploadApplicationId, {
        file,
        documentType: uploadType as UploadDocumentBodyDocumentType,
        description: `${documentLabel(uploadType)} added from the document archive`,
        documentDate: documentDate || undefined,
        expiryDate: expiryDate || undefined,
      });
      toast.success(`${documentLabel(uploadType)} uploaded`);
      setDocumentDate('');
      setExpiryDate('');
      onUploaded?.();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const row = (item: ArchiveDocument, old = false) => {
    const d = item.document || ({} as any);
    return (
      <tr key={(d.id || d.documentKey) + (old ? '-old' : '')} className={old ? 'text-muted' : ''}>
        <td>
          <div className="fw-semibold">{documentLabel(d.documentType)}</div>
          <div className="small text-muted text-break">
            {d.name}
            {d.directorName ? ` · ${d.directorName}` : ''}
            {d.period ? ` · ${formatDocumentPeriod(d.period)}` : ''}
          </div>
        </td>
        <td className="small">
          {formatDocumentDate(getDocumentUploadDate(d))}
          <div className="text-muted">{getDocumentUploader(d) || 'Unknown'}</div>
        </td>
        <td className="small">
          {d.documentDate ? <>Dated {formatDocumentDate(d.documentDate)}</> : <span className="text-muted">-</span>}
          {d.validUntil && <div className="text-muted">Valid until {formatDocumentDate(d.validUntil)}</div>}
        </td>
        <td>
          {old
            ? <span className="badge rounded-pill bg-light text-muted">Replaced {formatDocumentDate(d.supersededDate)}</span>
            : <DocumentValidityBadge uploadDate={getDocumentUploadDate(d)} status={d.validityStatus} validUntil={d.validUntil} rule={d.validityRule} />}
        </td>
        <td className="text-end text-nowrap">
          <button className="btn btn-sm btn-light me-1" disabled={!d.documentKey || preview.openingKey === d.documentKey}
            onClick={() => preview.open(d.documentKey, d.name)} title="View">
            <i className={`fa ${preview.openingKey === d.documentKey ? 'fa-spinner fa-spin' : 'fa-eye'}`}></i>
          </button>
          <button className="btn btn-sm btn-light" disabled={!d.documentKey || downloading === d.documentKey}
            onClick={() => download(d.documentKey, d.name)} title="Download">
            <i className={`fa ${downloading === d.documentKey ? 'fa-spinner fa-spin' : 'fa-download'}`}></i>
          </button>
        </td>
      </tr>
    );
  };

  return (
    <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: 14 }}>
      <div className="card-body">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
          <div className="d-flex gap-3">
            <div className="rounded-3 bg-success-subtle text-success-emphasis d-flex align-items-center justify-content-center"
              style={{ width: 42, height: 42 }}>
              <i className={`fa ${folder.icon}`}></i>
            </div>
            <div>
              <h6 className="fw-bold mb-0">{folder.title} <span className="badge bg-light text-dark fw-normal ms-1">{current.length}</span></h6>
              <small className="text-muted">{folder.description}</small>
            </div>
          </div>
          {uploadApplicationId && folder.uploadTypes.length > 0 && (
            <div className="d-flex flex-wrap align-items-end gap-2">
              <select className="form-select form-select-sm" style={{ width: 'auto' }} value={uploadType}
                onChange={e => { setUploadType(e.target.value); setDocumentDate(''); setExpiryDate(''); }}>
                {folder.uploadTypes.map(t => <option key={t} value={t}>{documentLabel(t)}</option>)}
              </select>
              {wantsDocumentDate && (
                <input type="date" className="form-control form-control-sm" style={{ width: 'auto' }} value={documentDate}
                  title={rule?.requireDocumentDate ? 'Date on the document (required)' : 'Date on the document'}
                  onChange={e => setDocumentDate(e.target.value)} />
              )}
              {wantsExpiry && (
                <input type="date" className="form-control form-control-sm" style={{ width: 'auto' }} value={expiryDate}
                  title="Expiry date, if it has one" onChange={e => setExpiryDate(e.target.value)} />
              )}
              <label className={`btn btn-sm btn-outline-success mb-0 ${uploading || dateMissing ? 'disabled' : ''}`}
                title={dateMissing ? 'Enter the date on the document first' : undefined}>
                <i className={`fa ${uploading ? 'fa-spinner fa-spin' : 'fa-upload'} me-1`}></i>Upload
                <input type="file" className="d-none" accept=".pdf,.jpg,.jpeg,.png" onChange={upload} disabled={uploading || dateMissing} />
              </label>
            </div>
          )}
        </div>
        {folder.uploadTypes.length === 0 && folder.key === 'DIRECTORS' && (
          <p className="small text-muted mb-2"><i className="fa fa-info-circle me-1"></i>Added on the application, where each document is kept against its director.</p>
        )}
        {current.length === 0 && previous.length === 0 ? (
          <p className="text-muted small mb-0">No documents in this folder.</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead className="small text-muted">
                <tr><th>Document</th><th>Uploaded</th><th>Document date</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {current.map(d => row(d))}
                {showPrevious && previous.map(d => row(d, true))}
              </tbody>
            </table>
            {previous.length > 0 && (
              <button className="btn btn-link btn-sm px-0" onClick={() => setShowPrevious(v => !v)}>
                <i className="fa fa-history me-1"></i>
                {showPrevious ? 'Hide' : 'Show'} {previous.length} previous version{previous.length === 1 ? '' : 's'}
              </button>
            )}
          </div>
        )}
      </div>
      <DocumentPreviewModal preview={preview.preview} onClose={preview.close} />
    </div>
  );
};

export const AttentionList: React.FC<{ items: { kind?: string; message?: string; loanReference?: string }[]; limit?: number }> =
  ({ items, limit }) => (
    <ul className="list-unstyled mb-0">
      {(limit ? items.slice(0, limit) : items).map((a, i) => (
        <li key={i} className="d-flex gap-2 py-1">
          <i className={`fa ${a.kind === 'MISSING' ? 'fa-exclamation-circle text-warning' : a.kind === 'EXPIRED' ? 'fa-exclamation-circle text-danger' : 'fa-clock-o text-warning'} mt-1`}></i>
          <span>{a.message}{a.loanReference && <span className="text-muted small"> · {a.loanReference}</span>}</span>
        </li>
      ))}
    </ul>
  );
